import useForm from "../../hooks/useForm";
import { Box, Button, Checkbox, Grid, ListItemText, Typography } from "@mui/material";
import ValidatorSelect from "../Validator/ValidatorSelect";
import { formValue } from "../../services/formServiceFunctions";
import MenuItem from "@mui/material/MenuItem";
import ValidatorDateField from "../Validator/ValidatorDatePicker";
import _ from "lodash";
import {
  dateFieldDefault,
  isTodayOrFutureDate,
  minStringLength,
  notNoneSelectValidator,
  requiredFieldDefault,
  requiredValidator,
  tipTapEditorDefault,
} from "../../services/validationRules";
import React, { ChangeEvent, useState } from "react";
import ValidatorTextField from "../Validator/ValidatorTextField";
import ButtonLoading from "../Button/ButtonLoading";
import ApiClient from "../../services/ApiClient";
import { activitiesFilterString } from "../../hooks/useActivities";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchCollection, queryKeys } from "../../services/ReactQuery/reactQueryService";
import { apiPost } from "../../services/Api/apiCall";
import { useCurrentUser } from "../../provider/CurrentUserProvider";
import { getProductData, ProductDataValue } from "../../services/Product/ProductService/productService";
import { MediaObject } from "../../types/MediaObject";
import { getMediaObjectFilters } from "../../services/mediaObjectService";
import LegalbirdTipTapEditorProvider from "../LbEditor/LegalbirdTipTapEditorProvider";
import LegalbirdTipTapEditor from "../LbEditor/LegalbirdTipTapEditor";
import AddTextBlock from "../LbEditor/Toolbar/AddTextBlock";
import { ProductUnion } from "../../types/ProductUnion";
import { createPortal } from "react-dom";
import dayjs from "dayjs";
import { InquiryToCustomer } from "../../types/InquiryToCustomer";
import { useDrawer } from "../../provider/DrawerProvider";
import { UseForm } from "../../types/UseForm";
import useDraftAutosave from "../../hooks/useDraftAutosave";
import useEffectWithoutMount from "../../hooks/useEffectWithoutMount";
import DraftAutosaveIndicator from "../GlobalDrawer/DraftAutosaveIndicator";

export default function AddInquiryToCustomerForm({
  handleClose,
  product,
  editorPlaceholder,
  conversationId,
  draft,
  overwrittenHandleSave,
}: {
  handleClose: Function;
  product: ProductUnion;
  editorPlaceholder?: string;
  conversationId?: number;
  draft?: Partial<InquiryToCustomer>;
  overwrittenHandleSave?: Function;
}) {
  const queryClient = useQueryClient();
  const currentUser = useCurrentUser();
  const [isLoading, setIsLoading] = useState(false);
  const [calendlyError, setCalendlyError] = useState(false);
  const initialValues = {
    inquiryType: draft?.inquiryType ?? "",
    deadline: draft?.deadline ?? dayjs().add(14, "d").format("YYYY-MM-DD"),
    overdueConsequences: draft?.overdueConsequences ?? "",
    inquiryTextAppointment: draft?.inquiryTextAppointment ?? "",
    inquiryTextInformation: draft?.inquiryTextInformation ?? "",
    inquiryTextDocuments: draft?.inquiryTextDocuments ?? "",
    requestedDocuments: draft?.requestedDocuments ?? [
      {
        documentName: "",
        mediaObjectId: null,
        temporaryFilesToCombine: [],
      },
    ],
    attachedMediaObjects: draft?.attachedMediaObjects ?? [],
  };
  const { drawerActionsNode, drawerHeaderNode } = useDrawer();

  const buildInquiryData = (currentValues: UseForm["values"]) => {
    let data = { ...currentValues };

    data.requestedDocuments = currentValues.requestedDocuments.filter(
      (document: { documentName: string }) => document.documentName.length > 0
    );
    data.productId = product.id;
    data.createdBy = currentUser["@id"];
    data.feedbackRecipient = currentUser["@id"];
    if (conversationId) {
      data.conversationId = conversationId;
    }
    return data;
  };

  const onSubmit = async ({ values }: { values: UseForm["values"] }) => {
    if (overwrittenHandleSave) {
      return;
    }
    setIsLoading(true);

    const data = buildInquiryData(values);

    if (values.inquiryType === "appointment") {
      const filters = {
        done: false,
        deleted: false,
      };
      const activityCollection = await ApiClient.get(
        "activities?" +
          activitiesFilterString(product, filters) +
          "&type[]=lawyer_counseling_call&type[]=calendly_event"
      );
      if (activityCollection["hydra:totalItems"] > 0) {
        setCalendlyError(true);
        setIsLoading(false);
        return;
      }
    }

    await apiPost("/inquiry_to_customers", data);
    await queryClient.invalidateQueries({
      queryKey: queryKeys.collections("inquiry_to_customers")
    });

    setIsLoading(false);
    handleClose();
  };

  const { values, errors, handleChange, handleDateChange, handleBlur, handleSubmit, registerValidators } = useForm({
    initialValues,
    onSubmit,
    disableValidation: !!overwrittenHandleSave,
  });

  const { saveState, scheduleSave } = useDraftAutosave({
    enabled: !!overwrittenHandleSave,
    persist: async () => {
      await overwrittenHandleSave?.(buildInquiryData(values));
    },
  });

  useEffectWithoutMount(() => scheduleSave(), [values]);

  const mediaObjectsFilter = getMediaObjectFilters(product, currentUser);
  const { data: mediaObjectsCollection } = useQuery({
    queryKey: queryKeys.collection("media_objects", mediaObjectsFilter),

    queryFn: () =>
      fetchCollection("media_objects", mediaObjectsFilter)
  });

  const mediaObjects = mediaObjectsCollection?.["hydra:member"];

  const addRequestedDocument = () => {
    let currentRequestedDocuments = [..._.get(values, "requestedDocuments")];
    currentRequestedDocuments.push({
      documentName: "",
      mediaObjectId: null,
      temporaryFilesToCombine: [],
    });
    handleChange({
      target: {
        name: "requestedDocuments",
        value: currentRequestedDocuments,
      },
    });
  };

  const handleChangeDocumentName = (event: ChangeEvent<HTMLInputElement>) => {
    let currentEvent = { ...event };
    currentEvent.target.value = event.target.value.replace(/[^äöüa-z 0-9_-]/gi, "");
    handleChange(currentEvent);
  };

  const documentHelperText = (index: number) => {
    const errorText = _.get(errors, "requestedDocuments[" + index + "].documentName");
    if (!!errorText) {
      return errorText;
    }
    const currentValue = _.get(values, "requestedDocuments[" + index + "].documentName");
    return `${currentValue.length}/100 Zeichen - keine Sonderzeichen`;
  };

  const inquiryTypeAvailability: ProductDataValue["inquiryTypeAvailability"] = getProductData(
    product.productClassName,
    "inquiryTypeAvailability"
  );

  if (!inquiryTypeAvailability || !drawerActionsNode) {
    return null;
  }

  return (
    <>
      <Grid container spacing={2}>
        {calendlyError && (
          <Grid size={12}>
            <Box
              sx={{
                color: "red",
                textAlign: "center"
              }}>
              Es gibt bereits einen anstehenden Telefontermin für diesen Fall. Bitte andere Anfrage-Art wählen, oder
              Modal schließen.
            </Box>
          </Grid>
        )}
        {Object.keys(errors).length > 0 && (
          <Grid size={12}>
            <Box
              sx={{
                color: "red",
                textAlign: "center"
              }}>
              Bitte prüfen Sie Ihre unten gemachten Angaben.
            </Box>
          </Grid>
        )}
        <Grid size={12}>
          <ValidatorSelect
            label={"Art der Anfrage"}
            name={"inquiryType"}
            value={formValue(values, "inquiryType")}
            onChange={(e) => {
              setCalendlyError(false);
              handleChange(e);
            }}
            onBlur={handleBlur}
            validators={notNoneSelectValidator}
            error={!!_.get(errors, "inquiryType")}
            helperText={_.get(errors, "inquiryType")}
            registerValidators={registerValidators}
          >
            <MenuItem value={"appointment"} disabled={!inquiryTypeAvailability.getForAppointment(product)}>
              Terminvereinbarung
            </MenuItem>
            <MenuItem
              value={"informationAndDocuments"}
              disabled={!inquiryTypeAvailability.getForInformationAndDocuments(product)}
            >
              Informationen und Dokumente
            </MenuItem>
            <MenuItem value={"documents"} disabled={!inquiryTypeAvailability.getForDocuments(product)}>
              Nur Dokumente
            </MenuItem>
            <MenuItem value={"information"} disabled={!inquiryTypeAvailability.getForInformation(product)}>
              Nur Informationen
            </MenuItem>
          </ValidatorSelect>
        </Grid>
        {_.get(values, "inquiryType") === "appointment" && (
          <Grid size={12}>
            <LegalbirdTipTapEditorProvider
              initialContent={initialValues.inquiryTextAppointment ?? ""}
              onUpdate={({ editor }) => {
                handleChange({
                  target: {
                    name: "inquiryTextAppointment",
                    value: editor.getHTML(),
                  },
                });
              }}
              placeholder={editorPlaceholder || "z.B. Ich möchte das neueste Schreiben mit Ihnen besprechen."}
            >
              <LegalbirdTipTapEditor additionalToolbarActions={[<AddTextBlock context={"inquiry"} />]} />
            </LegalbirdTipTapEditorProvider>
          </Grid>
        )}
        {["information", "informationAndDocuments"].includes(_.get(values, "inquiryType")) && (
          <Grid size={12}>
            <LegalbirdTipTapEditorProvider
              initialContent={initialValues.inquiryTextInformation ?? ""}
              onUpdate={({ editor }) => {
                handleChange({
                  target: {
                    name: "inquiryTextInformation",
                    value: editor.getHTML(),
                  },
                });
              }}
              placeholder={editorPlaceholder || "Benötigte Informationen"}
            >
              <LegalbirdTipTapEditor
                name={"inquiryTextInformation"}
                registerValidators={registerValidators}
                validators={tipTapEditorDefault}
                additionalToolbarActions={[<AddTextBlock context={"inquiry"} />]}
                error={!!errors["inquiryTextInformation"]}
                helperText={errors["inquiryTextInformation"]}
              />
            </LegalbirdTipTapEditorProvider>
          </Grid>
        )}
        {values.inquiryType === "informationAndDocuments" && (
          <Grid size={12}>
            <Typography sx={{ textAlign: "center" }} variant="h3">
              Benötigte Dokumente
            </Typography>
          </Grid>
        )}
        {["documents", "informationAndDocuments"].includes(_.get(values, "inquiryType")) && (
          <>
            <Grid size={12}>
              <LegalbirdTipTapEditorProvider
                initialContent={initialValues.inquiryTextDocuments ?? ""}
                onUpdate={({ editor }) => {
                  handleChange({
                    target: {
                      name: "inquiryTextDocuments",
                      value: editor.getHTML(),
                    },
                  });
                }}
                placeholder={editorPlaceholder || "Erklärung zu Dokumenten (optional)"}
              >
                <LegalbirdTipTapEditor additionalToolbarActions={[<AddTextBlock context={"inquiry"} />]} />
              </LegalbirdTipTapEditorProvider>
            </Grid>
            {_.get(values, "requestedDocuments").map((document: any, index: number) => {
              let validators: any[] = [
                {
                  validator: minStringLength,
                  params: [3],
                  message:
                    "Bitte geben Sie hier mindestens 3 Zeichen ein, oder wählen Sie eine andere Art der Anfrage aus.",
                },
              ];
              if (index === 0) {
                validators.push(requiredValidator("Bitte geben Sie hier den Namen für das benötigte Dokument an."));
              }
              return (
                <Grid key={index} size={12}>
                  <ValidatorTextField
                    label={"Name Dokument " + (index + 1)}
                    name={"requestedDocuments[" + index + "].documentName"}
                    value={formValue(values, "requestedDocuments[" + index + "].documentName")}
                    onChange={handleChangeDocumentName}
                    onBlur={handleBlur}
                    registerValidators={registerValidators}
                    validators={validators}
                    error={!!_.get(errors, "requestedDocuments[" + index + "].documentName")}
                    helperText={documentHelperText(index)}
                    margin={"normal"}
                    fullWidth
                    isHighlighted={false}
                    initialDependentValidationFields={[]}
                    slotProps={{
                      htmlInput: {
                        maxLength: 100,
                      },
                    }}
                  />
                </Grid>
              );
            })}
            {_.get(values, "requestedDocuments").length < 10 && (
              <Grid size={12}>
                <Button variant={"text"} onClick={addRequestedDocument}>
                  Weiteres Dokument
                </Button>
              </Grid>
            )}
          </>
        )}
        <Grid
          size={{
            xs: 12,
            sm: 6
          }}>
          <ValidatorDateField
            format="DD.MM.YYYY"
            label={"Fällig am"}
            name={"deadline"}
            onChange={(date: any) => handleDateChange(date, "deadline")}
            handleBlur={handleBlur}
            value={formValue(values, "deadline")}
            error={!!_.get(errors, "deadline")}
            helperText={
              _.get(errors, "deadline") || "3 Tage vor Ablauf der Frist wird automatisch bei Mandantschaft nachgefasst"
            }
            registerValidators={registerValidators}
            validators={[
              ...requiredFieldDefault,
              ...dateFieldDefault,
              {
                validator: isTodayOrFutureDate,
                message: "Es kann keine Anfrage in die Vergangenheit gestellt werden.",
              },
            ]}
            shouldDisableDate={(date) => {
              return !isTodayOrFutureDate(date);
            }}
            dependentValidationFields={[]}
            isHighlighted={false}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6
          }}>
          <ValidatorSelect
            label={"Bei fehlender Rückmeldung"}
            name={"overdueConsequences"}
            value={formValue(values, "overdueConsequences")}
            onChange={handleChange}
            onBlur={handleBlur}
            validators={notNoneSelectValidator}
            error={!!_.get(errors, "overdueConsequences")}
            helperText={_.get(errors, "overdueConsequences")}
            registerValidators={registerValidators}
          >
            <MenuItem value={"actionRequired"}>Weitere Bearbeitung notwendig</MenuItem>
            <MenuItem value={"noFurtherActionRequired"}>Keine weitere Bearbeitung notwendig</MenuItem>
          </ValidatorSelect>
        </Grid>
        {mediaObjects && (
          <Grid size={12}>
            <ValidatorSelect
              label={"Anhänge"}
              name={"attachedMediaObjects"}
              value={formValue(values, "attachedMediaObjects")}
              onChange={handleChange}
              onBlur={handleBlur}
              error={!!_.get(errors, "attachedMediaObjects")}
              helperText={_.get(errors, "attachedMediaObjects")}
              registerValidators={registerValidators}
              multiple={true}
              renderValue={(selected: string[]) => {
                return selected
                  .map((value) => _.find(mediaObjects, (mediaObject) => mediaObject.id === value)?.description)
                  .join(", ");
              }}
            >
              {mediaObjects.map((mediaObject: MediaObject) => (
                <MenuItem value={mediaObject.id} key={mediaObject.id}>
                  <Checkbox checked={values.attachedMediaObjects.includes(mediaObject.id)} />
                  <ListItemText primary={mediaObject.description || "Ohne Beschreibung"} />
                </MenuItem>
              ))}
            </ValidatorSelect>
          </Grid>
        )}
      </Grid>
      {overwrittenHandleSave
        ? drawerHeaderNode && createPortal(<DraftAutosaveIndicator saveState={saveState} />, drawerHeaderNode)
        : createPortal(
            <Grid container sx={{ width: "100%" }}>
              <Grid size={3} sx={{ display: { xs: "none", md: "block" } }} />
              <Grid size={{ xs: 12, md: 6 }} sx={{ marginTop: 6 }}>
                <ButtonLoading variant={"contained"} isLoading={isLoading} onClick={handleSubmit}>
                  Anfrage abschicken
                </ButtonLoading>
              </Grid>
            </Grid>,
            drawerActionsNode
          )}
    </>
  );
}
